const pillars = [
  {
    title: "Privacy Above All",
    icon: new URL("../assets/icons/shieldIcon.png", import.meta.url).href,
    description:
      "No data harvesting. No intrusive tracking. Your information and data belongs solely to you. Stay in control of your online presence with privacy-first design and features. Share only what you want, with whom you want."
  },
  {
    title: "Real, Tailored Safety",
    icon: new URL("../assets/icons/approvedUserIcon.png", import.meta.url).href,
    description:
      "A rigorous verification process designed to keep the space women-only, exclusive, and safe. We are building with care around moderation, consent, and image safety to create a network where women can connect without fear of harassment or misuse."
  },
  {
    title: "Intentional Growth, Careful Curation",
    icon: new URL("../assets/icons/growingIcon.png", import.meta.url).href,
    description:
      "Structured sharing and collaboration features within a diverse community. We're not about bots or influencers, we're about real people and real connections. "
  }
];

const interests = [
  "Events and Meetups",
  "Finding New Friends",
  "Wellbeing and Advice",
  "Safe Social Media",
  "Networking",
  "Other"
];

const heroTitles = [
  [{ text: "Social networking, for " }, { text: "women.", emphasis: true }],
  [{ text: "Safety isn't a feature, it's the " }, { text: "foundation.", emphasis: true }],
  [{ text: "Where women come " }, { text: "first.", emphasis: true }],
  [{ text: "Community", emphasis: true }, { text: " matters. You belong here." }],
  [{ text: "A " }, { text: "smarter", emphasis: true }, { text: " kind of social media." }]
];

let titleTimer;

export function renderHomePage() {
  return `
    <main id="home">
      <section class="hero" aria-labelledby="hero-title">
        <div class="hero-content">
          <p class="pill"><span class="status-dot" aria-hidden="true"></span>Verified applications now open</p>
          <h1 id="hero-title" class="typing-title" aria-live="polite"></h1>
          <p class="hero-copy">
            Escape the noise of traditional social media. Join a curated, women-only online
            network designed for genuine connection.
          </p>
          <div class="hero-actions">
            <a class="button primary" href="/#join" data-route>Join the Waitlist</a>
            <a class="button secondary" href="/#vision" data-route>Explore the Vision</a>
          </div>
        </div>

        <aside class="hero-art" aria-label="Community photography">
          <div class="image-frame">
            <img
              src="${new URL('../assets/images/priscilla-du-preez-9e4n5-xvvbg-unsplash.jpg', import.meta.url).href}"
              alt="Women gathered together in conversation"
            >
          </div>
          <div class="floating-card">
            <div class="floating-card-title">
              <img src="${new URL('../assets/icons/activeMyns.png', import.meta.url).href}" alt="" aria-hidden="true">
              <strong>Founding Myns</strong>
            </div>
            <p>Myns are what we call our network members: join early and shape the community!</p>
          </div>
        </aside>
      </section>

      <section class="section centered" id="vision" aria-labelledby="vision-title">
        <p class="eyebrow">Our Intent</p>
        <h2 id="vision-title">Beyond the Feed</h2>
        <div class="narrow-copy">
          <p>
            womyn.network was born from the realization that modern digital spaces are often
            hostile to women. We are building more than a platform; we are constructing a dynamic,
            bustling community to support building real connections.
          </p>
        </div>

        <div class="vision-detail-grid">
          <article class="vision-detail-card">
            <h3>The Problem</h3>
            <p>
              Women are disproportionately targeted by online harassment, data theft, and image
              misuse. Too many platforms ask women to absorb the risk while treating safety as an
              afterthought.
            </p>
          </article>
          <article class="vision-detail-card">
            <h3>Privacy First Identity</h3>
            <p>
              womyn.network separates your real identity from your social presence. Myns can build
              trust through intentional verification without giving the entire internet access to
              everything about them.
            </p>
          </article>
          <article class="vision-detail-card">
            <h3>Safe Sharing</h3>
            <p>
              Your photos, posts, and conversations should belong to you. We are designing around
              consent, agency, and safer sharing from the first version of the network.
            </p>
          </article>
          <article class="vision-detail-card">
            <h3>Authentic Connections</h3>
            <p>
              No ad-first feed. No performance theatre. womyn.network is built for genuine
              interaction, thoughtful discovery, and safer spaces for women to meet online.
            </p>
          </article>
        </div>

        <div class="access-panel">
          <h3>How Access Works</h3>
          <div class="access-steps">
            <article>
              <span>01</span>
              <h4>Secure an Invite</h4>
              <p>womyn.network is invite-only to keep the space intentional, safer, and high trust.</p>
            </article>
            <article>
              <span>02</span>
              <h4>Choose Your Username</h4>
              <p>Create a unique handle before joining the wider community and building your profile.</p>
            </article>
            <article>
              <span>03</span>
              <h4>Become a Myn</h4>
              <p>Access the feed, save your favorite posts, join discussions, chat, and make new friends.</p>
            </article>
          </div>
        </div>

        <div class="ethics-note">
          <p>
            womyn.network prioritises women's agency. We are building with care around data,
            consent, image safety, and moderation because nobody should have their identity or
            photos used without permission.
          </p>
        </div>
      </section>

      <section class="section soft" id="pillars" aria-labelledby="pillars-title">
        <div class="split-heading">
          <div>
            <h2 id="pillars-title">The Pillars of Our Ethos</h2>
            <p>
              Our architecture is built on foundations that ensure every myn feels seen, heard,
              and protected.
            </p>
          </div>
          <a class="learn-more-link" href="/contact" data-route>
            Learn More
            <img src="${new URL('../assets/icons/Arrow right.png', import.meta.url).href}" alt="" aria-hidden="true">
          </a>
        </div>

        <div class="interest-grid" id="interestGrid"></div>
      </section>

      <section class="section connect-section" aria-labelledby="connect-title">
        <p class="eyebrow">Ways to Connect</p>
        <h2 id="connect-title">A first step toward the network people actually need.</h2>
        <p class="connect-description">
          womyn.network is invite-only because belonging should feel intentional. Every myn is
          vetted before joining, creating a more trusted space where access feels earned,
          introductions feel safer, and the network stays high-signal as it grows.
        </p>

        <div class="feature-carousel" aria-label="Network features">
          <div class="feature-track">
            ${[
              "create Circles",
              "share posts safely",
              "follow your friends",
              "meet new Myns",
              "connect across the country",
              "get advice",
              "share your thoughts safely",
              "feel welcome",
              "meet new people",
              "find thoughtful support",
              "build trusted connections",
              "have fun with new friends"
            ]
              .concat([
                "create Circles",
                "share posts safely",
                "follow your friends",
                "meet new Myns",
                "connect across the country",
                "get advice",
                "share your thoughts safely",
                "feel welcome",
                "meet new people",
                "find thoughtful support",
                "build trusted connections",
                "have fun with new friends"
              ])
              .map((feature, index) => `<span${index > 11 ? ' aria-hidden="true"' : ""}>${feature}</span>`)
              .join("")}
          </div>
        </div>
      </section>

      <section class="section founding" id="founding" aria-labelledby="founding-title">
        <div class="placeholder-collage" aria-label="Founding myn photography">
          <div class="portrait-placeholder collage-image">
            <img src="${new URL('../assets/images/karl-magnuson-MvxMvPO3S1M-unsplash.jpg', import.meta.url).href}" alt="Portrait of a woman">
          </div>
          <div class="wide-placeholder collage-image">
            <img src="${new URL('../assets/images/vitaly-gariev-S-GwgDw0vjg-unsplash.jpg', import.meta.url).href}" alt="A group gathered together">
          </div>
          <div class="wide-placeholder collage-image">
            <img src="${new URL('../assets/images/resume-genius-i3n2Fj7qodw-unsplash.jpg', import.meta.url).href}" alt="Mentoring conversation at a table">
          </div>
        </div>

        <div class="founding-copy">
          <span class="rule"></span>
          <h2 id="founding-title">Shape the Network's Future</h2>
          <p>
            Our founding myns are more than early users; they are the architects of our culture.
            By joining now, you can influence platform features and community guidelines.
          </p>

          <ul class="benefit-list">
            <li>
              <img class="benefit-icon" src="${new URL('../assets/icons/foundingMynBadge.png', import.meta.url).href}" alt="" aria-hidden="true">
              <strong>Founding Myn Badge</strong>
              <span>A permanent mark of distinction on your profile.</span>
            </li>
            <li>
              <img class="benefit-icon" src="${new URL('../assets/icons/giftIcon.png', import.meta.url).href}" alt="" aria-hidden="true">
              <strong>Early Beta Access</strong>
              <span>Access tools and features before the wider rollout.</span>
            </li>
            <li>
              <img class="benefit-icon" src="${new URL('../assets/icons/chatIcon.png', import.meta.url).href}" alt="" aria-hidden="true">
              <strong>Real Influence</strong>
              <span>Vote on community initiatives and platform direction.</span>
            </li>
          </ul>

          <a class="button secondary" href="/#join" data-route>Become a Founding Myn today!</a>
        </div>
      </section>

      <section class="section join-section" id="join" aria-labelledby="join-title">
        <div class="join-content-wrapper">
          <form
          class="interest-form"
          id="interestForm"
          action="https://api.web3forms.com/submit"
          method="POST"
        >
          <input type="hidden" name="access_key" value="b72ab339-ae8e-400b-ac8c-3184f18a8c2f">
          <input type="hidden" name="subject" value="New womyn.network waitlist interest">
          <input type="checkbox" name="botcheck" class="botcheck" tabindex="-1" autocomplete="off">

          <div class="form-heading">
            <div class="invite-callout" aria-label="Invite code bonus">
              <span>Join now and get</span>
              <strong>+20 invite codes!</strong>
            </div>
            <h2 id="join-title">Claim Your Place</h2>
            <p>We curate our waitlist to maintain the quality of the network.</p>
          </div>

          <div class="form-row">
            <label>
              Name
              <input type="text" name="name" autocomplete="name" placeholder="Jane Doe" required>
            </label>
            <label>
              Email
              <input type="email" name="email" autocomplete="email" placeholder="jane@company.com">
            </label>
          </div>

          <div class="form-row">
            <label>
              Social platform
              <select name="social_platform">
                <option value="">Choose one</option>
                <option value="Instagram">Instagram</option>
                <option value="TikTok">TikTok</option>
                <option value="Facebook">Facebook</option>
                <option value="Discord">Discord</option>
                <option value="LinkedIn">LinkedIn</option>
              </select>
            </label>
            <label>
              Handle
              <input type="text" name="social_handle" placeholder="@yourhandle">
            </label>
          </div>

          <label class="label-with-note">
            <span>Location <span class="field-note">(optional)</span></span>
            <input type="text" name="location" autocomplete="address-level2" placeholder="Nearest city near you">
          </label>

          <fieldset>
            <legend>Interest areas</legend>
            <div class="checkbox-list" id="interestOptions"></div>
            <p class="form-helper">If Other please specify below!</p>
          </fieldset>

          <label>
            What are you looking for?
            <textarea
              name="message"
              rows="5"
              placeholder="Tell us what would make this network valuable for you..."
              required
            ></textarea>
          </label>

          <label class="info-checkbox">
            <input type="checkbox" name="wants_more_info" value="Yes">
            <span>
              Not sure yet? No worries! We're reaching out to every applicant. If you want more
              info before registering for official verification, tick this box and we'll have a
              chat &lt;3
            </span>
          </label>

          <button class="form-submit" type="submit">Apply to Join</button>
          <p class="form-status" id="formStatus" role="status" aria-live="polite"></p>
        </form>

        <a class="collab-callout" href="/contact#collaborate" data-route>
          <strong>Interested in collaborating?</strong>
          <span>
            We're expanding. Fill out this form
            <img src="${new URL('../assets/icons/Arrow right.png', import.meta.url).href}" alt="" aria-hidden="true">
          </span>
        </a>
        </div>
      </section>

      <section class="section faq" id="faq" aria-labelledby="faq-title">
        <div class="section-heading centered-heading" style="position: relative;">
          <a class="faq-callout" href="/contact" data-route>
            <strong>Got more questions?</strong>
            <span>Reach out &lt;3</span>
          </a>
          <h2 id="faq-title">Common Enquiries</h2>
          <span class="title-underline"></span>
        </div>

        <div class="faq-list">
          <article>
            <h3>How is the platform kept exclusive and verified?</h3>
            <p>
              We are still shaping the verification approach and gathering interest from people who
              want a safer, more intentional professional space. Verification will involve a mix of social media vetting, reference checks, and manual review to ensure a trusted community!
            </p>
          </article>
          <article>
            <h3>What makes this different from other social medias?</h3>
            <p>
              The focus is depth of connection and shared experience without the performative noise
              of traditional feeds. No ads, no data harvesting, and a design built around consent and safety from the ground up. You're not a product, you're a myn <3
            </p>
          </article>
          <article>
            <h3>When will the network launch?</h3>
            <p>
              Signing up the verification waitlist is the first step. Responses will guide the beta timeline and
              first myn experience. We're moving fast, but we want to get it right, so sign up to our mailing list for updates and announcements as we get closer to launch!
            </p>
          </article>
        </div>
      </section>

      <section class="newsletter-section" aria-labelledby="newsletter-title">
        <div class="newsletter-inner">
          <div>
            <p class="eyebrow">Stay close</p>
            <h2 id="newsletter-title">Subscribe to the newsletter</h2>
            <p>
              Get founding updates, community notes, and early access announcements as
              womyn.network takes shape. We'll also share info on upcoming events and opportunities to connect with other myns as we build!
            </p>
          </div>
          <div class="newsletter-form" id="newsletterForm"></div>
        </div>
      </section>
    </main>
  `;
}

export function afterHomeRender() {
  renderPillars();
  renderInterestOptions();
  startTitleTyping();
  bindWaitlistForm();
  loadBeehiivForm();
}

function renderPillars() {
  const interestGrid = document.querySelector("#interestGrid");

  if (!interestGrid) return;

  interestGrid.innerHTML = pillars
    .map(
      (pillar) => `
        <article class="interest-card">
          <span aria-hidden="true">
            <img src="${pillar.icon}" alt="">
          </span>
          <h3>${pillar.title}</h3>
          <p>${pillar.description}</p>
        </article>
      `
    )
    .join("");
}

function renderInterestOptions() {
  const interestOptions = document.querySelector("#interestOptions");

  if (!interestOptions) return;

  interestOptions.innerHTML = interests
    .map(
      (interest) => `
        <label>
          <input type="checkbox" name="interests[]" value="${interest}">
          ${interest}
        </label>
      `
    )
    .join("");
}

function bindWaitlistForm() {
  const interestForm = document.querySelector("#interestForm");
  const formStatus = document.querySelector("#formStatus");

  if (!interestForm) return;

  interestForm.addEventListener("submit", (event) => {
    const formData = new FormData(interestForm);
    const selectedInterests = formData.getAll("interests[]");
    const email = formData.get("email").trim();
    const socialHandle = formData.get("social_handle").trim();
    const socialPlatform = formData.get("social_platform");

    if (selectedInterests.length === 0) {
      event.preventDefault();
      formStatus.textContent = "Please choose at least one interest area.";
      return;
    }

    if (!email && !socialHandle) {
      event.preventDefault();
      formStatus.textContent = "Please add an email or social handle so we can reach you.";
      return;
    }

    if (socialHandle && !socialPlatform) {
      event.preventDefault();
      formStatus.textContent = "Please choose a social platform for your handle.";
    }
  });
}

function getTitleText(parts) {
  return parts.map((part) => part.text).join("");
}

function renderTypedTitle(parts, characterCount) {
  const typingTitle = document.querySelector("#hero-title");

  if (!typingTitle) return;

  typingTitle.replaceChildren();

  let remainingCharacters = characterCount;

  parts.forEach((part) => {
    if (remainingCharacters <= 0) return;

    const text = part.text.slice(0, remainingCharacters);
    const node = part.emphasis ? document.createElement("span") : document.createTextNode(text);

    if (part.emphasis) {
      node.textContent = text;
    }

    typingTitle.append(node);
    remainingCharacters -= text.length;
  });
}

function startTitleTyping() {
  if (!document.querySelector("#hero-title")) return;

  window.clearTimeout(titleTimer);

  let titleIndex = 0;
  let characterCount = 0;
  let isDeleting = false;
  let completedTitles = 0;
  const totalAnimatedTitles = heroTitles.length * 2;

  function tick() {
    const title = heroTitles[titleIndex];
    const fullLength = getTitleText(title).length;

    renderTypedTitle(title, characterCount);

    if (!isDeleting && characterCount < fullLength) {
      characterCount += 1;
      titleTimer = window.setTimeout(tick, 105);
      return;
    }

    if (!isDeleting && characterCount === fullLength) {
      completedTitles += 1;

      if (window.innerWidth <= 720 && titleIndex === 0) return;
      if (completedTitles > totalAnimatedTitles && titleIndex === 0) return;

      isDeleting = true;
      titleTimer = window.setTimeout(tick, 3600);
      return;
    }

    if (isDeleting && characterCount > 0) {
      characterCount -= 1;
      titleTimer = window.setTimeout(tick, 58);
      return;
    }

    titleIndex =
      completedTitles >= totalAnimatedTitles
        ? 0
        : (titleIndex + 1) % heroTitles.length;
    isDeleting = false;
    titleTimer = window.setTimeout(tick, 650);
  }

  tick();
}

function loadBeehiivForm() {
  const newsletterForm = document.querySelector("#newsletterForm");

  if (!newsletterForm) return;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://subscribe-forms.beehiiv.com/v3/loader.js";
  script.dataset.beehiivForm = "c4de236e-28fd-47cf-a08f-25b4d8adadff";

  newsletterForm.replaceChildren(script);
}
